import React, { useState, useRef, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import { getOutsourceResources } from '../../../services/masterDataService';

const TIME_UNITS = [
  { value: 'hours', label: 'Hours', toHours: 1 },
  { value: 'days', label: 'Days', toHours: 9 },
  { value: 'months', label: 'Months', toHours: 270 }
];

const ROLE_LABELS = { senior: 'Senior', mid: 'Mid-Level', junior: 'Junior', lead: 'Lead', architect: 'Architect', manager: 'Manager' };

const SPF_PERCENTAGE = 0.225; // 22.5%

const OutsourceSelector = ({ selectedOutsource, onOutsourceChange, currency, syncChanges = [] }) => {
  const [masterResources, setMasterResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // Initialize local state from persisted selectedOutsource so restored data stays live
  const [quantities, setQuantities] = useState(() => {
    const init = {};
    selectedOutsource?.forEach(r => { if (r?.masterId) init[r.masterId] = r?.quantity ?? 1; });
    return init;
  });
  const [durations, setDurations] = useState(() => {
    const init = {};
    selectedOutsource?.forEach(r => { if (r?.masterId) init[r.masterId] = r?.duration ?? 1; });
    return init;
  });
  const [timeUnits, setTimeUnits] = useState(() => {
    const init = {};
    selectedOutsource?.forEach(r => { if (r?.masterId) init[r.masterId] = r?.timeUnit ?? 'hours'; });
    return init;
  });
  const [nationalities, setNationalities] = useState(() => {
    const init = {};
    selectedOutsource?.forEach(r => { if (r?.masterId) init[r.masterId] = r?.nationality ?? 'expat'; });
    return init;
  });
  const [healthInsuranceEnabled, setHealthInsuranceEnabled] = useState(() => {
    const init = {};
    selectedOutsource?.forEach(r => { if (r?.masterId) init[r.masterId] = r?.healthInsuranceEnabled ?? false; });
    return init;
  });
  const [healthInsuranceValues, setHealthInsuranceValues] = useState(() => {
    const init = {};
    selectedOutsource?.forEach(r => { if (r?.masterId) init[r.masterId] = r?.healthInsuranceValue ?? 0; });
    return init;
  });

  // Sync local state when selectedOutsource is replaced (e.g. loading a saved estimate)
  const prevIdsRef = useRef(null);
  useEffect(() => {
    const currentIds = (selectedOutsource || [])?.map(r => r?.masterId)?.sort()?.join(',');
    // Include quantity and duration in signature so edits to QTY on the same set of resources are detected
    const currentSig = (selectedOutsource || [])
      ?.map(r => `${r?.masterId}:${r?.quantity ?? 1}:${r?.duration ?? 1}:${r?.timeUnit ?? 'hours'}:${r?.nationality ?? 'expat'}`)
      ?.sort()
      ?.join('|');
    if (prevIdsRef?.current !== null && prevIdsRef?.current !== currentSig) {
      const newQuantities = {};
      const newDurations = {};
      const newTimeUnits = {};
      const newNationalities = {};
      const newHIEnabled = {};
      const newHIValues = {};
      (selectedOutsource || [])?.forEach(r => {
        if (r?.masterId) {
          newQuantities[r.masterId] = r?.quantity ?? 1;
          newDurations[r.masterId] = r?.duration ?? 1;
          newTimeUnits[r.masterId] = r?.timeUnit ?? 'hours';
          newNationalities[r.masterId] = r?.nationality ?? 'expat';
          newHIEnabled[r.masterId] = r?.healthInsuranceEnabled ?? false;
          newHIValues[r.masterId] = r?.healthInsuranceValue ?? 0;
        }
      });
      setQuantities(newQuantities);
      setDurations(newDurations);
      setTimeUnits(newTimeUnits);
      setNationalities(newNationalities);
      setHealthInsuranceEnabled(newHIEnabled);
      setHealthInsuranceValues(newHIValues);
    }
    prevIdsRef.current = currentSig;
  }, [selectedOutsource]);

  const dropdownRef = useRef(null);

  const currencySymbol = currency?.symbol || 'OMR';

  useEffect(() => {
    const fetchResources = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await getOutsourceResources();
        setMasterResources(data || []);
      } catch (err) {
        console.error('Failed to load outsource resources:', err);
        setError('Failed to load outsource resources from master data.');
      } finally {
        setLoading(false);
      }
    };
    fetchResources();
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef?.current && !dropdownRef?.current?.contains(e?.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filteredResources = masterResources?.filter(r =>
    r?.name?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
    r?.role?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
    r?.specialization?.toLowerCase()?.includes(searchQuery?.toLowerCase())
  );

  const isSelected = (id) => selectedOutsource?.some(r => r?.masterId === id);

  const getHours = (duration, timeUnit) => {
    const unit = TIME_UNITS?.find(u => u?.value === timeUnit);
    return parseFloat(duration || 1) * (unit?.toHours || 1);
  };

  // Calculate cost per hour based on the selected time unit — mirrors Master Data Management logic
  // Master Data: Daily = Monthly / 30, Hourly = Daily / 9 = Monthly / 270
  const getCostPerHour = (resource, timeUnit) => {
    const unit = timeUnit || 'hours';
    if (unit === 'days') {
      if (resource?.dailySalary > 0) return parseFloat((resource?.dailySalary / 9)?.toFixed(4));
      if (resource?.monthlySalary > 0) return parseFloat(((resource?.monthlySalary / 30) / 9)?.toFixed(4));
      return 0;
    }
    if (unit === 'months') {
      if (resource?.monthlySalary > 0) return parseFloat((resource?.monthlySalary / 270)?.toFixed(4));
      return 0;
    }
    // hours (default)
    if (resource?.hourlySalary > 0) return parseFloat(resource?.hourlySalary?.toFixed(4));
    if (resource?.monthlySalary > 0) return parseFloat((resource?.monthlySalary / 270)?.toFixed(4));
    return 0;
  };

  // SPF is calculated as: Monthly Salary * 22.5% (only for Omani nationals)
  // Returns the MONTHLY SPF amount; callers must prorate for other units.
  const getSPFMonthly = (resource, nationality) => {
    if (nationality !== 'omani') return 0;
    const monthlySalary = resource?.monthlySalary || 0;
    return monthlySalary * SPF_PERCENTAGE;
  };

  // Prorate a monthly fixed cost to match the selected time unit.
  // Monthly → full value; Daily → value / 30; Hours → value / 30 / 9
  const prorateMonthlyAmount = (monthlyAmount, timeUnit) => {
    if (!monthlyAmount) return 0;
    if (timeUnit === 'days') return parseFloat(monthlyAmount) / 30;
    if (timeUnit === 'hours') return parseFloat(monthlyAmount) / 30 / 9;
    return parseFloat(monthlyAmount); // months
  };

  const calcTotalCost = (quantity, duration, timeUnit, resource, nationality, hiEnabled, hiValue) => {
    const hours = getHours(duration, timeUnit);
    const costPerHour = getCostPerHour(resource, timeUnit);
    const baseCost = (parseFloat(quantity) || 1) * hours * costPerHour;
    const spfMonthly = getSPFMonthly(resource, nationality);
    const spfCost = spfMonthly * (parseFloat(duration) || 1) * (parseFloat(quantity) || 1);
    const hiMonthly = hiEnabled ? (parseFloat(hiValue) || 0) : 0;
    const healthInsurance = hiMonthly * (parseFloat(duration) || 1) * (parseFloat(quantity) || 1);
    return baseCost + spfCost + healthInsurance;
  };

  const handleToggle = (resource) => {
    if (isSelected(resource?.id)) {
      onOutsourceChange(selectedOutsource?.filter(r => r?.masterId !== resource?.id));
    } else {
      const qty = parseFloat(quantities?.[resource?.id]) || 1;
      const duration = parseFloat(durations?.[resource?.id]) || 1;
      const timeUnit = timeUnits?.[resource?.id] || 'hours';
      // Use master data nationality/SPF/HI defaults if available
      const nationality = nationalities?.[resource?.id] || resource?.nationality || 'expat';
      const hiEnabled = healthInsuranceEnabled?.[resource?.id] ?? resource?.healthInsuranceEnabled ?? false;
      const hiValue = parseFloat(healthInsuranceValues?.[resource?.id] ?? resource?.healthInsuranceValue) || 0;
      const hours = getHours(duration, timeUnit);
      const costPerHour = getCostPerHour(resource, timeUnit);
      const spfMonthly = getSPFMonthly(resource, nationality);
      const spfCost = spfMonthly * duration * qty;
      const hiMonthly = hiEnabled ? hiValue : 0;
      const healthInsurance = hiMonthly * duration * qty;
      const entry = {
        id: Date.now() + Math.random(),
        masterId: resource?.id,
        name: resource?.name,
        role: resource?.role,
        specialization: resource?.specialization || '',
        monthlySalary: resource?.monthlySalary,
        dailySalary: resource?.dailySalary,
        hourlySalary: resource?.hourlySalary,
        utilizationDefault: resource?.utilizationDefault,
        quantity: qty,
        duration,
        timeUnit,
        hours,
        costPerHour,
        nationality,
        spfCost,
        healthInsuranceEnabled: hiEnabled,
        healthInsuranceValue: hiValue,
        totalCost: qty * hours * costPerHour + spfCost + healthInsurance,
      };
      onOutsourceChange([...selectedOutsource, entry]);
    }
  };

  const updateSelected = (masterId, patch) => {
    const idx = selectedOutsource?.findIndex(r => r?.masterId === masterId);
    if (idx >= 0) {
      const updated = [...selectedOutsource];
      const item = { ...updated?.[idx], ...patch };
      const hours = getHours(item?.duration || 1, item?.timeUnit || 'hours');
      item.hours = hours;
      const masterRes = masterResources?.find(r => r?.id === masterId);
      const spfMonthly = getSPFMonthly(masterRes || item, item?.nationality || 'expat');
      const spfCost = spfMonthly * (item?.duration || 1) * (item?.quantity || 1);
      item.spfCost = spfCost;
      const hiMonthly = item?.healthInsuranceEnabled ? (parseFloat(item?.healthInsuranceValue) || 0) : 0;
      const healthInsurance = hiMonthly * (item?.duration || 1) * (item?.quantity || 1);
      item.totalCost = (item?.quantity || 1) * hours * (item?.costPerHour || 0) + spfCost + healthInsurance;
      updated[idx] = item;
      onOutsourceChange(updated);
    }
  };

  const handleQuantityChange = (masterId, val) => {
    setQuantities(prev => ({ ...prev, [masterId]: val }));
    updateSelected(masterId, { quantity: parseFloat(val) || 1 });
  };

  const handleDurationChange = (masterId, val) => {
    setDurations(prev => ({ ...prev, [masterId]: val }));
    updateSelected(masterId, { duration: parseFloat(val) || 1 });
  };

  const handleTimeUnitChange = (masterId, val) => {
    setTimeUnits(prev => ({ ...prev, [masterId]: val }));
    const masterRes = masterResources?.find(r => r?.id === masterId);
    const newCostPerHour = masterRes ? getCostPerHour(masterRes, val) : undefined;
    const dur = durations?.[masterId] || selectedOutsource?.find(r => r?.masterId === masterId)?.duration || 1;
    const hours = getHours(dur, val);
    const patch = { timeUnit: val, hours };
    if (newCostPerHour !== undefined) patch.costPerHour = newCostPerHour;
    updateSelected(masterId, patch);
  };

  const handleNationalityChange = (masterId, val) => {
    setNationalities(prev => ({ ...prev, [masterId]: val }));
    updateSelected(masterId, { nationality: val });
  };

  const handleHealthInsuranceToggle = (masterId, enabled) => {
    setHealthInsuranceEnabled(prev => ({ ...prev, [masterId]: enabled }));
    updateSelected(masterId, { healthInsuranceEnabled: enabled });
  };

  const handleHealthInsuranceValueChange = (masterId, val) => {
    setHealthInsuranceValues(prev => ({ ...prev, [masterId]: val }));
    updateSelected(masterId, { healthInsuranceValue: parseFloat(val) || 0 });
  };

  const handleRemove = (id) => {
    onOutsourceChange(selectedOutsource?.filter(r => r?.id !== id));
  };

  const formatDuration = (resource) => {
    if (resource?.timeUnit && resource?.duration) {
      const unitLabel = TIME_UNITS?.find(u => u?.value === resource?.timeUnit)?.label || 'Hours';
      return `${resource?.duration} ${unitLabel}`;
    }
    return `${resource?.hours || 0}h`;
  };

  const selectedCount = selectedOutsource?.length || 0;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <label className="block text-sm font-medium text-foreground">
          Outsource Resources
        </label>
        {error && (
          <span className="text-xs text-error flex items-center gap-1">
            <Icon name="AlertCircle" size={12} />
            {error}
          </span>
        )}
      </div>
      {/* Dropdown Trigger */}
      <div className="relative" ref={dropdownRef}>
        <button
          type="button"
          onClick={() => setDropdownOpen(prev => !prev)}
          disabled={loading}
          className="w-full flex items-center justify-between px-3 py-2.5 bg-background border border-border rounded-lg text-sm text-foreground hover:border-primary/50 focus:outline-none focus:ring-2 focus:ring-primary/30 transition-all disabled:opacity-60"
        >
          <span className={selectedCount === 0 ? 'text-muted-foreground' : ''}>
            {loading
              ? 'Loading resources...'
              : selectedCount === 0
              ? 'Select outsource resources...'
              : `${selectedCount} resource${selectedCount > 1 ? 's' : ''} selected`}
          </span>
          {loading
            ? <Icon name="Loader2" size={16} className="text-muted-foreground animate-spin" />
            : <Icon name={dropdownOpen ? 'ChevronUp' : 'ChevronDown'} size={16} className="text-muted-foreground" />
          }
        </button>

        {dropdownOpen && (
          <div className="absolute z-50 w-full mt-1 bg-card border border-border rounded-lg shadow-lg overflow-hidden">
            {/* Search */}
            <div className="p-2 border-b border-border">
              <div className="relative">
                <Icon name="Search" size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <input
                  type="text"
                  placeholder="Search resources..."
                  value={searchQuery}
                  onChange={e => setSearchQuery(e?.target?.value)}
                  className="w-full pl-8 pr-3 py-1.5 text-sm bg-background border border-border rounded-md focus:outline-none focus:ring-1 focus:ring-primary/50 text-foreground placeholder:text-muted-foreground"
                  autoFocus
                />
              </div>
            </div>

            {/* Items List */}
            <div className="max-h-52 overflow-y-auto">
              {filteredResources?.length === 0 && (
                <div className="px-3 py-3 text-sm text-muted-foreground text-center">
                  {masterResources?.length === 0
                    ? 'No outsource resources in master data. Add them in Master Data Management.' :'No resources found'}
                </div>
              )}
              {filteredResources?.map(resource => {
                const checked = isSelected(resource?.id);
                const selEntry = selectedOutsource?.find(r => r?.masterId === resource?.id);
                const currentUnit = timeUnits?.[resource?.id] ?? selEntry?.timeUnit ?? 'hours';
                const currentNationality = nationalities?.[resource?.id] ?? selEntry?.nationality ?? 'expat';
                const currentHIEnabled = healthInsuranceEnabled?.[resource?.id] ?? selEntry?.healthInsuranceEnabled ?? false;
                const currentHIValue = healthInsuranceValues?.[resource?.id] ?? selEntry?.healthInsuranceValue ?? 0;
                return (
                  <div key={resource?.id} className="px-3 py-2 hover:bg-muted/50 transition-colors">
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        id={`out-cb-${resource?.id}`}
                        checked={checked}
                        onChange={() => handleToggle(resource)}
                        className="w-4 h-4 rounded border-border text-primary accent-primary cursor-pointer"
                      />
                      <label htmlFor={`out-cb-${resource?.id}`} className="flex-1 cursor-pointer">
                        <div className="text-sm font-medium text-foreground">{resource?.name}</div>
                        <div className="text-xs text-muted-foreground">
                          {ROLE_LABELS?.[resource?.role] || resource?.role}
                          {resource?.specialization ? ` · ${resource?.specialization}` : ''}
                          {resource?.hourlySalary > 0
                            ? ` · ${currencySymbol} ${resource?.hourlySalary?.toLocaleString('en-US', { minimumFractionDigits: 3, maximumFractionDigits: 3 })}/hr`
                            : resource?.monthlySalary > 0
                            ? ` · ${currencySymbol} ${resource?.monthlySalary?.toLocaleString('en-US', { minimumFractionDigits: 3, maximumFractionDigits: 3 })}/mo`
                            : ''}
                          {resource?.utilizationDefault ? ` · ${resource?.utilizationDefault}% util` : ''}
                        </div>
                      </label>
                    </div>
                    {checked && (
                      <div className="mt-2 ml-6 space-y-2">
                        <div className="flex gap-2">
                          <div className="flex-1">
                            <label className="text-xs text-muted-foreground mb-0.5 block">Quantity</label>
                            <input
                              type="number"
                              min="1"
                              value={quantities?.[resource?.id] ?? selEntry?.quantity ?? 1}
                              onChange={e => handleQuantityChange(resource?.id, e?.target?.value)}
                              className="w-full px-2 py-1 text-xs border border-border rounded bg-background text-foreground focus:outline-none focus:ring-1 focus:ring-primary/50"
                            />
                          </div>
                          <div className="flex-1">
                            <label className="text-xs text-muted-foreground mb-0.5 block">Duration</label>
                            <input
                              type="number"
                              min="1"
                              value={durations?.[resource?.id] ?? selEntry?.duration ?? 1}
                              onChange={e => handleDurationChange(resource?.id, e?.target?.value)}
                              className="w-full px-2 py-1 text-xs border border-border rounded bg-background text-foreground focus:outline-none focus:ring-1 focus:ring-primary/50"
                            />
                          </div>
                          <div className="flex-1">
                            <label className="text-xs text-muted-foreground mb-0.5 block">Unit</label>
                            <select
                              value={currentUnit}
                              onChange={e => handleTimeUnitChange(resource?.id, e?.target?.value)}
                              className="w-full px-2 py-1 text-xs border border-border rounded bg-background text-foreground focus:outline-none focus:ring-1 focus:ring-primary/50"
                            >
                              {TIME_UNITS?.map(u => <option key={u?.value} value={u?.value}>{u?.label}</option>)}
                            </select>
                          </div>
                        </div>
                        {/* Nationality */}
                        <div>
                          <label className="text-xs text-muted-foreground mb-0.5 block">Nationality</label>
                          <select
                            value={currentNationality}
                            onChange={e => handleNationalityChange(resource?.id, e?.target?.value)}
                            className="w-full px-2 py-1 text-xs border border-border rounded bg-background text-foreground focus:outline-none focus:ring-1 focus:ring-primary/50"
                          >
                            <option value="expat">Expat</option>
                            <option value="omani">Omani</option>
                          </select>
                        </div>
                        {/* SPF info for Omani */}
                        {currentNationality === 'omani' && (
                          <div className="flex items-center gap-1.5 px-2 py-1.5 bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded text-xs text-blue-700 dark:text-blue-300">
                            <Icon name="Info" size={11} />
                            <span>SPF (22.5%) applied: {currencySymbol} {(getSPFMonthly(resource, 'omani'))?.toLocaleString('en-US', { minimumFractionDigits: 3, maximumFractionDigits: 3 })}/mo</span>
                          </div>
                        )}
                        {/* Health Insurance */}
                        <div className="space-y-1">
                          <div className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              id={`hi-cb-${resource?.id}`}
                              checked={currentHIEnabled}
                              onChange={e => handleHealthInsuranceToggle(resource?.id, e?.target?.checked)}
                              className="w-3.5 h-3.5 rounded border-border text-primary accent-primary cursor-pointer"
                            />
                            <label htmlFor={`hi-cb-${resource?.id}`} className="text-xs text-muted-foreground cursor-pointer">
                              Include Health Insurance
                            </label>
                          </div>
                          {currentHIEnabled && (
                            <div>
                              <label className="text-xs text-muted-foreground mb-0.5 block">Health Insurance Value ({currencySymbol})</label>
                              <input
                                type="number"
                                min="0"
                                step="0.001"
                                value={currentHIValue}
                                onChange={e => handleHealthInsuranceValueChange(resource?.id, e?.target?.value)}
                                className="w-full px-2 py-1 text-xs border border-border rounded bg-background text-foreground focus:outline-none focus:ring-1 focus:ring-primary/50"
                                placeholder="0.000"
                              />
                            </div>
                          )}
                        </div>
                        {currentUnit !== 'hours' && (
                          <p className="text-xs text-muted-foreground">
                            = {getHours(durations?.[resource?.id] ?? selEntry?.duration ?? 1, currentUnit)?.toLocaleString()} hours
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Footer */}
            <div className="border-t border-border px-3 py-2 bg-muted/20">
              <p className="text-xs text-muted-foreground flex items-center gap-1">
                <Icon name="Info" size={11} />
                Manage resources in <span className="font-medium text-primary">Master Data Management → Outsource Resources</span>
              </p>
            </div>
          </div>
        )}
      </div>
      {/* Selected Items */}
      {selectedOutsource?.length > 0 ? (
        <div className="space-y-2">
          {selectedOutsource?.map((resource) => {
            const baseCost = (resource?.quantity || 1) * (resource?.hours || getHours(resource?.duration || 1, resource?.timeUnit || 'hours')) * (resource?.costPerHour || 0);
            const spfCost = resource?.spfCost || 0;
            const healthInsurance = resource?.healthInsuranceEnabled ? (parseFloat(resource?.healthInsuranceValue) || 0) : 0;
            const totalCost = baseCost + spfCost + healthInsurance;
            const resourceMasterId = resource?.masterId || resource?.id;
            const hasChange = syncChanges?.some(
              c => c?.type === 'outsource' && (c?.id === resourceMasterId || c?.name?.toLowerCase() === resource?.name?.toLowerCase())
            );
            return (
              <div
                key={resource?.id}
                className="flex items-start justify-between gap-2 p-3 bg-card border border-border rounded-lg"
              >
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-foreground truncate flex items-center gap-1.5">
                    {resource?.name}
                    {hasChange && (
                      <span
                        className="relative group flex-shrink-0 cursor-default"
                        title="Master Data updated — click Review Changes to apply"
                      >
                        <span className="text-amber-500 text-base leading-none select-none">⚠</span>
                        <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 z-50 hidden group-hover:flex whitespace-nowrap bg-gray-900 text-white text-xs rounded px-2 py-1 shadow-lg pointer-events-none">
                          Master Data updated — click Review Changes to apply
                        </span>
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-muted-foreground mt-1 break-words">
                    {ROLE_LABELS?.[resource?.role] || resource?.role}
                    {resource?.specialization ? ` · ${resource?.specialization}` : ''}
                    {` · ${resource?.quantity || 1} × ${formatDuration(resource)} @ ${currencySymbol} ${(resource?.costPerHour || 0)?.toLocaleString('en-US', { minimumFractionDigits: 3, maximumFractionDigits: 3 })}/hr`}
                    {resource?.nationality === 'omani' ? ' · Omani' : ' · Expat'}
                  </div>
                  {(spfCost > 0 || healthInsurance > 0) && (
                    <div className="text-xs text-muted-foreground mt-0.5 space-x-2">
                      {spfCost > 0 && <span className="text-blue-600 dark:text-blue-400">SPF: {currencySymbol} {spfCost?.toLocaleString('en-US', { minimumFractionDigits: 3, maximumFractionDigits: 3 })}</span>}
                      {healthInsurance > 0 && <span className="text-green-600 dark:text-green-400">HI: {currencySymbol} {healthInsurance?.toLocaleString('en-US', { minimumFractionDigits: 3, maximumFractionDigits: 3 })}</span>}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <div className="text-sm font-semibold text-foreground whitespace-nowrap">
                    {currencySymbol} {totalCost?.toLocaleString('en-US', { minimumFractionDigits: 3, maximumFractionDigits: 3 })}
                  </div>
                  <button
                    onClick={() => handleRemove(resource?.id)}
                    className="text-error hover:text-error/80 transition-smooth"
                    aria-label="Remove resource"
                  >
                    <Icon name="Trash2" size={18} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="text-center py-6 text-muted-foreground text-sm">
          No outsource resources selected. Use the dropdown above to add resources.
        </div>
      )}
    </div>
  );
};

export default OutsourceSelector;
